import { Component, type ReactNode } from "react";
import { View } from "react-native";

import { PressableTarget } from "@/ui/PressableTarget";
import { Text } from "@/ui/Text";

import { useScreenStyles } from "./screenStyles";

type Props = {
  children: ReactNode;
  /** Called before the boundary resets: lazyWithRetry's retry(). */
  onRetry: () => void;
};

/**
 * Catches a failed lazy load (a chunk that did not download) and offers Try again (FR-043). The
 * themed general error fallback for every layout comes with US6 (T118/T119).
 */
export class ChunkErrorBoundary extends Component<Props, { failed: boolean }> {
  override state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  retry = () => {
    this.props.onRetry();
    this.setState({ failed: false });
  };

  override render() {
    return this.state.failed ? (
      <ChunkError onRetry={this.retry} />
    ) : (
      this.props.children
    );
  }
}

function ChunkError({ onRetry }: { onRetry: () => void }) {
  const styles = useScreenStyles();
  return (
    <View style={styles.screen} testID="chunk-error">
      <View style={styles.body} role="main">
        <Text variant="heading" accessibilityRole="header" aria-level={1}>
          This page didn&apos;t load
        </Text>
        <Text tone="muted" style={styles.message}>
          Check your connection and try again.
        </Text>
        <PressableTarget onPress={onRetry}>Try again</PressableTarget>
      </View>
    </View>
  );
}
